/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import AmountToWords from "../../../../components/_utils/AmountToWords.js";
import CommonAlertCard from "../../../../components/_utils/CommonAlertCard/index.jsx";
import CommonAssignLabelPopup from "../../../../components/_utils/CommonPopups/CommonAssignLabelPopup/index.jsx";
import SmallPopup from "../../../../components/_utils/CommonSmallPopup";
import SmallPopupWithBulletPoints from "../../../../components/_utils/CommonSmallPopupWithBulletPoint/index.jsx";
import PrimaryStepper from "../../../../components/_utils/CommonSteppers/PrimaryStepper";
import CommonToast from "../../../../components/_utils/CommonToast";
import TopNavComponent from "../../../../components/_utils/CommonTopNav/TopNavComponent/index.jsx";
import SvgComponent from "../../../../components/_utils/SVGComponent";
import InternalAccountViewAllPopup from "../../../../components/Accounts/InternalAccount/InternalAccountViewAllPopup/index.jsx";
import Loader from "../../../../components/Loader/Index.jsx";
import InternalTransactionStepAccount from "../../../../components/Transactions/InternalTransaction/InternalTransactionCreate/InternalTransactionStepAccount";
import InternalTransactionStepAdditionalDetails from "../../../../components/Transactions/InternalTransaction/InternalTransactionCreate/InternalTransactionStepAdditionalDetails";
import InternalTransactionStepAmount from "../../../../components/Transactions/InternalTransaction/InternalTransactionCreate/InternalTransactionStepAmount";
import InternalTransactionStepConfirm from "../../../../components/Transactions/InternalTransaction/InternalTransactionCreate/InternalTransactionStepConfirm";
import InternalRecreateTxnStepAdditionalDetails from "../../../../components/Transactions/InternalTransaction/InternalTransactionRecreate/InternalRecreateTxnStepAdditionalDetails";
import InternalRecreateTxnStepAmountPaymentMethod from "../../../../components/Transactions/InternalTransaction/InternalTransactionRecreate/InternalRecreateTxnStepAmountPaymentMethod";
import InternalRecreateTxnStepSourceDestination from "../../../../components/Transactions/InternalTransaction/InternalTransactionRecreate/InternalRecreateTxnStepSourceDestination";
import InternalTransactionViewAllPopup from "../../../../components/Transactions/InternalTransaction/InternalTransactionViewAllPopup/index.jsx";
import {
  ownerAccountsViewAll,
  resetOwnerBalance,
} from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification.js";
import {
  bankLookUp,
  getAllCategories,
  panLookUp,
  uploadFile,
} from "../../../../redux/features/sharedResources/sharedResources";
import {
  internalTransactionsCreate,
  internalTransactionsDetailsById,
  internalTransactionsRecreate,
  internalTransactionsViewAll,
} from "../../../../redux/features/transactions/internalTransactions/index.js";
import {
  getOutsideBankingHoursLabel,
  isOutsideBankingHours,
} from "../../../../utils/bankingHours.js";
import {
  GlobalConfiguration,
  IntervalConfiguration,
  PaginationConfiguration,
  PaymentMethodAnimationConfiguration,
  ScreenSizeConfiguration,
} from "../../../../utils/configurationProvider";
import { MONTHS } from "../../../../utils/dateConstants.js";
import globalText from "../../../../utils/globalText";
import Validation from "../../../../utils/validation.js";
import "./index.css";

/*                          CONSTANTS & PURE HELPERS                          */

const {
  lengthCheckZero: ZERO,
  lengthCheckOne: ONE,
  lengthCheckTwo: TWO,
  lengthCheckThree: THREE,
} = GlobalConfiguration;

const RTGS_MIN_AMOUNT = 200000;
const COPY_KEYS = { AMOUNT: "TRANSFER_AMOUNT" };
const EMPTY_COPY_DATA = { title: "", dataType: "", value: "" };
const RESET_COPY_STATE = {
  isCopyDirect: false,
  isCopyClick: false,
  isPaste: {},
};

const NON_TRIMMED_TYPES = [
  "date",
  "year",
  "toggle",
  "time",
  "quantity",
  "from_year_to_year",
  "month_year",
  "date_range",
  "multi_select_list",
  "upload",
];

// data types that need their picker UI state synced when a value is copied
const PICKER_COPY_TYPES = [
  "time",
  "date",
  "quantity",
  "toggle",
  "month_year",
  "from_year_to_year",
  "date_range",
];

// previous-txn data_type -> field data_types it can be pasted into
const PASTE_TARGETS = { numeric: ["numeric", "string"], string: ["string"] };
const SIMPLE_COPY_TYPES = ["hyperlink", "string", "numeric"];

const BALANCE_KEY_BY_RADIO = {
  destinationAcc: "destinationAccountBalance",
  sourceAcc: "ownerBankBalance",
};

const COMMON_STEPPER_CONFIG = {
  previousEnableBgColor: "var(--primary-nav-act)",
  previousDisableBgColor: "var(--text-disabled)",
  previousEnableTextColor: "var(--text-primary)",
  previousHoverOption: "white",
  nextHoverOption: "black",
};

const omit = (obj, ...keys) =>
  Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));

const uniq = (...lists) => [...new Set(lists.flatMap((l) => l || []))];

const isPresent = (v) => v !== "" && v !== undefined;

const isValidHttpUrl = (url) =>
  /^(https?:\/\/)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:[0-9]{1,5})?(\/.*)?$/.test(
    url,
  );

const readSessionLabels = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key)) || [];
  } catch {
    return [];
  }
};

/** Splits a flat label list into owner labels / other labels. */
const splitLabels = (all = [], ownerLabels = []) => ({
  ownerLabels: all.filter((l) => ownerLabels.includes(l)),
  labels: all.filter((l) => !ownerLabels.includes(l)),
});

const getAmountWords = (amount) => {
  const n = parseFloat(amount);
  if (isNaN(n)) return "";
  const words = AmountToWords(n);
  return n > 0 && n <= 1 ? words.replace("rupees", "rupee") : words;
};

const parseDateString = (value) => {
  if (!value || typeof value !== "string") return null;
  if (value.includes("/")) {
    const [day, month, year] = value.split("/").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const isEmptyValue = (value) =>
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === ZERO);

const isMandatoryFieldFilled = ({ data_type, value }) => {
  switch (data_type) {
    case "multi_select_list":
      return Array.isArray(value) && value.length > 0;
    case "quantity":
      return typeof value === "number" && value > 0;
    case "from_year_to_year":
      return isPresent(value?.from) && isPresent(value?.to);
    case "date_range":
      return isPresent(value?.start_date) && isPresent(value?.end_date);
    case "toggle":
      return value === true;
    case "numeric": {
      if (value === "" || value === undefined || value === null) return false;
      const n = Number(value);
      return !isNaN(n) && n !== 0;
    }
    default:
      return !!value && value !== "Not given";
  }
};

/** Category config fields + entered values -> mergeCategory. */
const buildMergeCategory = (fields, additionalData = {}) =>
  fields?.map((field) => {
    const value = additionalData[field?.name];

    const hasValue =
      value !== undefined &&
      (Array.isArray(value) ? value.length > ZERO : value !== "");

    if (hasValue) {
      return {
        ...field,
        value: field?.data_type === "numeric" ? String(value) : value,
      };
    }

    switch (field?.data_type) {
      case "multi_select_list":
        return { ...field, value: [] };
      case "toggle":
        return { ...field, value: false };
      case "hyperlink":
      case "upload":
        return { ...field, value: "" };
      case "time":
        return field;
      default:
        return field?.mandatory !== "Y" ? { ...field, value: "" } : field;
    }
  });

/** Previous txn's additional data -> "copy state" map used by the recreate UI. */
const buildInitialAdditionalDetails = (additionalData = []) =>
  additionalData.reduce((result, { name, value, data_type }) => {
    if (data_type === "toggle") {
      result[name] = { isCopyDirect: value === false, name, data: value };
      return result;
    }
    if (
      value === null ||
      value === "" ||
      value === undefined ||
      value?.length === ZERO
    ) {
      return result;
    }
    result[name] = SIMPLE_COPY_TYPES.includes(data_type)
      ? { isCopyClick: false, isPaste: {}, name, data: value }
      : { isCopyDirect: false, name, data: value };
    return result;
  }, {});

const areAdditionalValuesEqual = (currentValue, originalValue) => {
  if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
    if (currentValue.length !== originalValue.length) return false;
    const original = [...originalValue].sort();
    return [...currentValue].sort().every((v, i) => v === original[i]);
  }

  if (currentValue && typeof currentValue === "object") {
    if ("start_date" in currentValue) {
      return (
        currentValue.start_date === originalValue?.start_date &&
        currentValue.end_date === originalValue?.end_date
      );
    }
    if ("from" in currentValue) {
      return (
        currentValue.from === originalValue?.from &&
        currentValue.to === originalValue?.to
      );
    }
  }

  return currentValue === originalValue;
};

const RECREATE_SESSION_KEY = "recreate_int_txn_id";

/** Old transaction id from sessionStorage (details page) or router state. */
const readRecreateTxnId = (routeState) => {
  let stored = null;
  try {
    const raw = sessionStorage.getItem(RECREATE_SESSION_KEY);
    stored = raw ? JSON.parse(raw) : null;
  } catch {
    stored = null;
  }

  const storedId = typeof stored === "string" ? stored : stored?.txn_id;
  const stateId =
    routeState?.type === "refactorFlow" ? routeState?.txn_id : null;

  return storedId || stateId || null;
};

const clearRecreateSession = () => {
  try {
    sessionStorage.removeItem(RECREATE_SESSION_KEY);
  } catch {
    /* ignore */
  }
};

/*                                 COMPONENT                                  */

const InternalTransactionCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const stepperHeightRef = useRef(null);
  const hasAutoFillInteracted = useRef(false);

  const currentAccountDetails = location?.state;
  const isReminderFlow = currentAccountDetails?.flowType === "ReminderFlow";
  const flow = isReminderFlow ? "ReminderFlow" : "createTxn";
  const reminder_remark = isReminderFlow
    ? currentAccountDetails?.txnDetails?.reminderRemark
    : "";

  /* -------------------------------- state -------------------------------- */
  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(null);
  const [removeClassName, setRemoveClassName] = useState(true);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [stepperHeight, setStepperHeight] = useState();

  const [isNeft, setIsNeft] = useState(false);
  const [isRtgs, setIsRtgs] = useState(false);
  const [amountInWords, setAmountInWords] = useState("");
  const [slideAnimation, setSlideAnimation] = useState(false);
  const [selectedRadioValue, setSelectedRadioValue] = useState("customname");

  const [isConfidential, setIsConfidential] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [similarTransaction, setSimilarTransaction] = useState(false);

  // account step
  const [isInternalAccountPopupOpen, setIsInternalAccountPopupOpen] =
    useState(false);
  const [openOwner, setOpenOwner] = useState(false);
  const [activeType, setActiveType] = useState("pan");
  const [removePan, setRemovePan] = useState("");
  const [removeIfsc, setRemoveIfsc] = useState("");
  const [removeAccount, setRemoveAccount] = useState("");
  const [haveBankAssociate, setHaveBankAssociate] = useState(0);
  const [havePANAssociate, setHavePANAssociate] = useState(0);

  // additional-details step
  const [additionalData, setAdditionalData] = useState({});
  const [quantity, setQuantity] = useState({});
  const [timeData, setTimeData] = useState({});
  const [startDate, setStartDate] = useState({});
  const [endDate, setEndDate] = useState({});
  const [hyperLink, setHyperLink] = useState({});
  const [selectedValue, setSelectedValue] = useState({});
  const [selectedValueMonth, setSelectedValueMonth] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previewImages, setPreviewImages] = useState({});
  const [copyData, setCopyData] = useState(EMPTY_COPY_DATA);

  // popups
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelPopupLoading, setLabelPopupLoading] = useState(false);
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  const [showBankingHoursPopup, setShowBankingHoursPopup] = useState(false);
  const [showConfirmLeavePopup, setShowConfirmLeavePopup] = useState(false);
  const [showReminderRemark, setShowReminderRemark] = useState(false);
  const [isAssociatedMode, setIsAssociatedMode] = useState(false);
  // account step

  // recreate (refactor) flow
  const [refactor, setRefactor] = useState(false);
  const [autoFill, setAutoFill] = useState(false);
  const [isHoverEnabled, setIsHoverEnabled] = useState(true);
  const [refactorTransactionData, setRefactorTransactionData] = useState([]);
  const [refactorCopyAmount, setRefactorCopyAmount] = useState(false);
  const [refactorAccountDetail, setRefactorAccountDetail] = useState(false);
  const [refactorPaymentMethod, setRefactorPaymentMethod] = useState(false);
  const [
    refactorDestinationAccountDetail,
    setRefactorDestinationAccountDetail,
  ] = useState(false);
  const [initialRefactorAdditionalDetail, setInitialRefactorAdditionalDetail] =
    useState({});

  const [txnPayLoad, setTxnPayLoad] = useState({
    bankAccountName: currentAccountDetails?.bank_account_holder_full_name || "",
    beneAccountId: currentAccountDetails?.id || null,
    bankAccountIFSC: currentAccountDetails?.ifsc_code || "",
    bankAccountNo: currentAccountDetails?.bank_acc_no || "",
    amount: "",
    settleType: "",
    bankLogo: currentAccountDetails?.bank_name || "",
    labels: [],
    ownerLabels: [],
    pan: currentAccountDetails?.pan || "",
    ownerId: null,
    ownerBankIFSC: null,
    ownerBankBalance: 0,
    ownerLastUpdated: null,
    ownerBankLogo: null,
    ownerBankAccountNo: null,
    ownerBankAccountName: null,
    selectedCategory:
      currentAccountDetails?.category_ids?.category_name || null,
    mergeCategory: null,
    amountInWords: "",
    destinationAccountBalance: 0,
    destinationAcountOwnerId: null,
    isDestinationOwnerActive: true,
    destinationAcountOwnerLastUpdated: null,
  });

  const [selectedLabelsCreate, setSelectedLabelsCreate] = useState(() =>
    uniq(txnPayLoad.ownerLabels, txnPayLoad.labels),
  );
  const [labels, setLabels] = useState(() =>
    uniq(
      txnPayLoad.labels,
      txnPayLoad.ownerLabels,
      readSessionLabels("internalTxnLabels"),
      readSessionLabels("internalAccountLabels"),
    ),
  );

  /* ------------------------------- selectors ------------------------------ */
  const { panLookupData, bankLookupData, categoriesData } = useSelector(
    (state) => state.sharedResources,
  );
  const services = useSelector((state) => state.refreshToken.services);
  const {
    internalTransactionsDetailsLoading,
    internalTransactionsDetailsData,
  } = useSelector((state) => state.internalTransactions);

  const [recreateTxnId] = useState(() => readRecreateTxnId(location?.state));

  /* ------------------------------- derived -------------------------------- */
  const isWindow = window.innerWidth >= ScreenSizeConfiguration?.mobileScreen;

  const categories = categoriesData?.categories || [];
  const internalCategory =
    categories.find((c) => c?.name === "Internal") ||
    categories.find((c) => c?.name?.trim()?.toLowerCase() === "internal");

  const shouldShowAssociatedAccounts =
    (haveBankAssociate > ONE || havePANAssociate > ONE) &&
    txnPayLoad?.bankAccountNo &&
    txnPayLoad?.bankAccountIFSC;

  const animationClassS =
    direction === "next" ? "animate-slide-next" : "animate-slide-prev";

  const steps = [
    globalText.SOURCE_AND_DESTINATION_ACCOUNT,
    globalText.ADDITIONAL_DETAILS_AND_LABELS,
    globalText.TRANSFER_AMOUNT,
    globalText.CONFIRM_AND_CREATE,
  ];

  const shouldShowBankingHoursAlert =
    isOutsideBankingHours() && [ZERO, THREE].includes(activeStep);

  const config =
    internalTransactionsDetailsData?.transaction?.[0]
      ?.confidential_transaction !== "N";

  const allLabels = uniq(txnPayLoad?.ownerLabels, txnPayLoad?.labels);
  const prevLabels = refactorTransactionData?.labels || [];
  const labelsCopied =
    initialRefactorAdditionalDetail?.["__labels__"]?.isCopyDirect;
  const displayLabels = prevLabels.length ? prevLabels.join(", ") : "No Labels";

  /* ------------------------------ small helpers --------------------------- */
  const patchRefactorDetail = (key, patch) =>
    setInitialRefactorAdditionalDetail((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));

  const resetScroll = () => {
    if (containerRef?.current) containerRef.current.scrollTop = ZERO;
  };

  const triggerStepAnimation = (dir) => {
    setDirection(dir);
    setRemoveClassName(false);
    setTimeout(
      () => setRemoveClassName(true),
      IntervalConfiguration?.intervalSmall,
    );
  };

  const toggleAutoFill = () => {
    hasAutoFillInteracted.current = true;
    setAutoFill(!autoFill);
  };

  const showMissingSourceAccountToast = () => {
    toast.dismiss();
    CommonToast({
      text: "Unable to find source account.",
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
      buttonNeeded: false,
    });
  };

  const showAmountLimitToast = (comparison) =>
    CommonToast({
      text: (
        <>
          Transaction amount should be {comparison} <br /> INR 2 lakhs.
        </>
      ),
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
    });

  /* ------------------------------ create / next --------------------------- */
  const buildAdditionalData = async (fields = []) => {
    const hasUploadField = fields.some(
      (f) => f?.data_type === "upload" && f?.value !== "Not given",
    );
    const uploadResponses = hasUploadField
      ? await Promise.all(
          Object.values(uploadedFiles).map((file) =>
            dispatch(uploadFile({ file })),
          ),
        )
      : [];

    let uploadIndex = ZERO;

    return fields.map((field) => {
      const { data_type, value } = field;

      if (data_type === "numeric") return { ...field, value: Number(value) };
      if (data_type === "toggle") return omit(field, "description");

      const isEmpty =
        value === "Not given" ||
        value === "" ||
        (data_type === "quantity" && value <= ZERO) ||
        (data_type === "multi_select_list" &&
          Array.isArray(value) &&
          value.length === ZERO);
      if (isEmpty) return omit(field, "value");

      if (data_type === "upload") {
        return {
          ...omit(field, "description"),
          value: uploadResponses?.[uploadIndex++]?.payload,
        };
      }
      return field;
    });
  };

  const buildDuplicateCheckPayload = () => ({
    page_no: ONE,
    page_item_count: PaginationConfiguration?.paginationSmall,
    amount: txnPayLoad?.amount,
    owner_account_id: txnPayLoad?.ownerId,
    internal_account_id: txnPayLoad?.beneAccountId,
    payment_method: txnPayLoad?.settleType?.toUpperCase(),
    category_id: txnPayLoad?.selectedCategory?.id,
    confidential_transaction: isConfidential,
    labels: [...(txnPayLoad?.labels || []), ...(txnPayLoad?.ownerLabels || [])],
  });

  const handleCreateTransaction = async () => {
    if (isSubmitting) return;

    if (!services?.includes("CInternalTxn")) {
      navigate("/feature_not_assigned");
      return;
    }

    setIsSubmitting(true);

    try {
      const internalCreateTxnData = {
        page_no: ONE,
        page_item_count: PaginationConfiguration?.paginationSmall,
        amount: txnPayLoad?.amount,
        owner_account_id: txnPayLoad?.ownerId,
        internal_account_id: txnPayLoad?.beneAccountId,
        payment_method: txnPayLoad?.settleType?.toUpperCase(),
        category_id: txnPayLoad?.selectedCategory?.id,
        confidential_transaction: isConfidential,
        labels: [
          ...(txnPayLoad?.labels || []),
          ...(txnPayLoad?.ownerLabels || []),
        ],
      };

      if (txnPayLoad?.mergeCategory?.length) {
        internalCreateTxnData.additional_data = {
          data: await buildAdditionalData(txnPayLoad.mergeCategory),
        };
      }

      const store = await dispatch(
        internalTransactionsCreate({ internalCreateTxnData }),
      );

      if (store?.payload?.status === true) {
        // Mark the old (rejected) transaction as refactored.
        if (refactor && recreateTxnId) {
          await dispatch(internalTransactionsRecreate({ id: recreateTxnId }));
        }

        clearRecreateSession();

        navigate(
          `/internal_transaction_dashboard/create_internal_transaction/success/${store?.payload?.transaction?.[0]?.txn_id}`,
        );
        return;
      }

      sessionStorage?.setItem(
        "internalRejected",
        JSON.stringify(
          store?.payload?.data?.errors ?? store?.payload?.data?.error,
        ),
      );
      navigate(
        "/internal_transaction_dashboard/create_internal_transaction/failed",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    resetScroll();
    hasAutoFillInteracted.current = false;
    setAutoFill(false);

    if (activeStep === TWO) {
      setTransactionLoading(true);
      setIsSubmitting(true);

      const res = await dispatch(
        internalTransactionsViewAll(buildDuplicateCheckPayload()),
      );
      const hasDuplicates = res?.payload?.transactions?.length > ZERO;

      setSimilarTransaction(hasDuplicates);
      setIsSubmitting(false);

      if (hasDuplicates) {
        toast.dismiss();
        CommonToast({
          text: (
            <>
              Similar transaction found. Click check <br />
              to view.
            </>
          ),
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: true,
          btnProps: {
            enabledBgColor: "var(--negative-card)",
            enabledTextColor: "var(--text-primary)",
            btnText: "Check",
            imgColor: "var(--text-primary)",
            handleFunction: async () => {
              setShowSimilarModal(true);
            },
            icon: ICONS.ARROW_RIGHT_WHITE,
            btnType: "button",
            isDisabled: false,
            hoverOption: "black",
          },
        });
      }

      setActiveStep((prev) => prev + ONE);
      setTransactionLoading(false);
      triggerStepAnimation("next");

      return;
    }
    if (activeStep < steps.length - ONE) {
      setActiveStep((prev) => prev + ONE);
      triggerStepAnimation("next");
      return;
    }
    if (similarTransaction) {
      setShowSimilarModal(true);
      return;
    }
    await handleCreateTransaction();
  };

  const isFormDirty = () =>
    activeStep > ZERO ||
    txnPayLoad.ownerId !== null ||
    isConfidential !== false;

  const leavePage = () => {
    clearRecreateSession();
    navigate(-1);
  };

  const handleBack = () => {
    resetScroll();
    hasAutoFillInteracted.current = false;

    if (activeStep === ZERO) {
      if (isFormDirty()) setShowConfirmLeavePopup(true);
      else leavePage();
      return;
    }
    setAutoFill(false);
    setActiveStep((prev) => Math.max(prev - ONE, ZERO));
    triggerStepAnimation("previous");
  };

  /* ------------------------------- accounts ------------------------------- */
  // shared by manual destination selection and "autofill destination" (recreate)
  const loadDestinationOwner = async ({ accountNumber, ifsc, panNo }) => {
    dispatch(panLookUp({ pan_number: panNo }));
    dispatch(bankLookUp({ ifsc, bank_account_no: accountNumber }));

    const res = await dispatch(
      ownerAccountsViewAll({
        bank_account_no: accountNumber,
        pan: panNo,
        ifsc,
      }),
    );
    const owner = res?.payload?.owner_accounts?.[0];

    return {
      owner,
      payload: {
        isDestinationOwnerActive: owner?.connected_banking === "Y",
        destinationAccountBalance: owner?.owner_account_balance,
        destinationAcountOwnerId: owner?.id,
        destinationAcountOwnerLastUpdated:
          owner?.owner_account_balance_last_fetched_at_in_words,
        ...(internalCategory && {
          selectedCategory: internalCategory,
          mergeCategory: internalCategory?.configuration?.data,
        }),
      },
    };
  };

  const closeInternalAccountPopup = () => {
    setIsInternalAccountPopupOpen(false);
    setIsAssociatedMode(false);
    setRemovePan("");
    setRemoveAccount("");
    setRemoveIfsc("");
  };

  const handleInternalAccountSelection = async (dataItem) => {
    if (!dataItem?.id) return;

    setRefactorDestinationAccountDetail(false);

    const {
      id: beneAccountId,
      name: accountName,
      bank_acc_no: accountNumber,
      ifsc_code: ifsc,
      bank_name: bankLogo,
      pan_no: panNo,
      labels: accountLabels,
    } = dataItem;

    const { owner, payload } = await loadDestinationOwner({
      accountNumber: accountNumber?.trim(),
      ifsc: ifsc?.trim(),
      panNo,
    });
    // same as before: no owner account -> destination is not applied
    if (!owner) return;

    setTxnPayLoad((prev) => ({
      ...prev,
      bankAccountName: accountName?.trim(),
      beneAccountId,
      bankAccountIFSC: ifsc?.trim(),
      bankAccountNo: accountNumber?.trim(),
      bankLogo,
      pan: panNo,
      labels: accountLabels || [],
      ...payload,
    }));
    closeInternalAccountPopup();
  };

  const handleOpenInternalAccountModal = () => {
    setIsAssociatedMode(false);
    setActiveType("pan");
    setRemovePan("");
    setRemoveAccount("");
    setRemoveIfsc("");
    setIsInternalAccountPopupOpen(true);
  };

  const handleOpenAssociatedAccounts = () => {
    const tab = txnPayLoad?.pan ? "pan" : "bank";

    setActiveType(tab);
    setRemovePan(tab === "pan" ? txnPayLoad?.pan || "" : "");
    setRemoveAccount(tab === "bank" ? txnPayLoad?.bankAccountNo || "" : "");
    setRemoveIfsc(tab === "bank" ? txnPayLoad?.bankAccountIFSC || "" : "");

    setIsAssociatedMode(true);
    setIsInternalAccountPopupOpen(true);
  };

  const associatedAccountsText = [
    "This includes only active (approved, pending) accounts linked to the provided destination account.",
    "Click here to view and change the destination account, if required.",
  ];

  const handleOwnerSelection = (name, acc, ifsc, logo, ownerId) =>
    setTxnPayLoad((prev) => ({
      ...prev,
      ownerId,
      ownerBankAccountName: name,
      ownerBankAccountNo: acc,
      ownerBankIFSC: ifsc,
      ownerBankLogo: logo,
    }));

  const handleDestinationAccountSelection = async () => {
    if (refactorDestinationAccountDetail) return;

    const d = refactorTransactionData;
    if (["rejected", "blocked"].includes(d?.internal_account_approval_status))
      return;

    if (
      !d?.internal_account_id ||
      !d?.internal_account_bank_acc_no ||
      !d?.internal_account_ifsc_code
    ) {
      showMissingSourceAccountToast();
      return;
    }

    const { payload } = await loadDestinationOwner({
      accountNumber: d.internal_account_bank_acc_no,
      ifsc: d.internal_account_ifsc_code,
      panNo: d.internal_account_pan_no,
    });

    setTxnPayLoad((prev) => ({
      ...prev,
      bankAccountName: d.internal_account_name,
      beneAccountId: d.internal_account_id,
      bankAccountIFSC: d.internal_account_ifsc_code,
      bankAccountNo: d.internal_account_bank_acc_no,
      bankLogo: d.internal_account_bank_name,
      pan: d.internal_account_pan_no,
      ...payload,
    }));
    setRefactorDestinationAccountDetail(true);
  };

  const handleSourceAccountSelection = () => {
    if (refactorAccountDetail) return;

    const d = refactorTransactionData;
    if (!d?.owner_account_id || !d?.owner_account_bank_acc_no) {
      showMissingSourceAccountToast();
      return;
    }

    setRefactorAccountDetail(true);
    setTxnPayLoad((prev) => ({
      ...prev,
      ownerId: d.owner_account_id,
      ownerBankIFSC: d.owner_account_ifsc_code,
      ownerBankBalance: d.owner_account_balance,
      ownerBankLogo: d.owner_account_bank_name,
      ownerLastUpdated: d.owner_account_balance_last_fetched_at_in_words,
      ownerBankAccountNo: d.owner_account_bank_acc_no,
      settleType: "NEFT",
      ownerBankAccountName: d.owner_account_bank_name,
    }));
  };

  /* -------------------------------- labels -------------------------------- */
  const handleCopyLabels = (labelsToCopy) =>
    setTxnPayLoad((prev) => ({
      ...prev,
      ...splitLabels(labelsToCopy, prev.ownerLabels),
    }));

  const handleClose = (finalLabels) => {
    setShowLabelModal(false);
    const labelsToUse = Array.isArray(finalLabels)
      ? finalLabels
      : selectedLabelsCreate;
    setSelectedLabelsCreate(labelsToUse);
    setTxnPayLoad((prev) => ({
      ...prev,
      ...splitLabels(labelsToUse, prev.ownerLabels),
    }));
  };

  /* -------------------------------- amount -------------------------------- */
  const handleDivBtn = (value) => {
    setSelectedRadioValue(value);
    const balanceKey = BALANCE_KEY_BY_RADIO[value];
    if (!balanceKey) return;

    const amount = Math.abs(txnPayLoad[balanceKey]);
    const words = getAmountWords(amount);
    setTxnPayLoad((prev) => ({ ...prev, amount, amountInWords: words }));
    setAmountInWords(words);
  };

  const handleAmountChange = (input) => {
    const { name, value } = input?.target ? input.target : input;
    const trimmedValue = value?.toString()?.trim() ?? "";

    if (!trimmedValue) {
      setTxnPayLoad((prev) => ({ ...prev, amount: "", amountInWords: "" }));
      return;
    }
    if (trimmedValue[0] === "0") return;

    const numericValue = trimmedValue.replace(/[^0-9.]/g, "");
    const enteredAmount = parseFloat(numericValue || ZERO);

    if (
      enteredAmount ===
      parseFloat(Math.abs(txnPayLoad.destinationAccountBalance))
    ) {
      setSelectedRadioValue("destinationAcc");
    } else if (
      enteredAmount === parseFloat(Math.abs(txnPayLoad.ownerBankBalance))
    ) {
      setSelectedRadioValue("sourceAcc");
    } else {
      setSelectedRadioValue("customname");
    }

    if (
      name !== "amount" ||
      !(Validation.allowedAmount(numericValue) || !numericValue.length)
    )
      return;

    const words = getAmountWords(numericValue);
    const settleType =
      parseFloat(numericValue) > RTGS_MIN_AMOUNT ? "rtgs" : "neft";

    if (
      (settleType === "neft" && !isNeft) ||
      (settleType === "rtgs" && !isRtgs)
    ) {
      setSlideAnimation(true);
      setTimeout(
        () => setSlideAnimation(false),
        PaymentMethodAnimationConfiguration.slideDuration,
      );
    }
    setIsNeft(settleType === "neft");
    setIsRtgs(settleType === "rtgs");
    setTxnPayLoad((prev) => ({
      ...prev,
      amount: numericValue,
      amountInWords: words,
      settleType,
    }));
    setAmountInWords(words);
  };

  const handleNeftClick = () => {
    if (Number(txnPayLoad?.amount) > RTGS_MIN_AMOUNT)
      showAmountLimitToast("less than");
  };

  const handleRtgsClick = () => {
    if (Number(txnPayLoad?.amount) <= RTGS_MIN_AMOUNT)
      showAmountLimitToast("more than");
  };

  /* ------------- additional details: ONE path for typing AND copying ------- */
  // Single entry point for every change to additionalData
  // (typed by user, copied via button, pasted from previous txn).
  const handleInputChangeCreate = (event, dataTypeArg) => {
    const target = event?.target || event;
    if (!target?.name) return;
    event?.preventDefault?.();

    const { name, value } = target;
    const type = target.data_type || target.data_typeChan || dataTypeArg;

    if (type === "numeric" && String(value ?? "").length > 15) return;

    let nextValue =
      NON_TRIMMED_TYPES.includes(type) || typeof value !== "string"
        ? value
        : value.replace(/^\s+/, "");

    if (type === "month_year" && value?.month && value?.year) {
      const month = String(
        new Date(`1 ${value.month}`).getMonth() + 1,
      ).padStart(2, "0");
      nextValue = `${month}/${value.year}`;
    } else {
      const field = txnPayLoad?.selectedCategory?.configuration?.data?.find(
        (item) => item.name === name,
      );
      if (field?.data_type === "numeric") {
        if (value === "") nextValue = "";
        else if (/^\d+$/.test(String(value))) nextValue = Number(value);
        else return;
      }
    }

    setAdditionalData((prev) => ({ ...prev, [name]: nextValue }));

    if (isEmptyValue(nextValue)) patchRefactorDetail(name, RESET_COPY_STATE);
  };

  // Keeps the picker components' own state in sync with a copied value.
  const syncPickerState = (name, dataType, value) => {
    const fieldKey = `${name}_${dataType}`;

    switch (dataType) {
      case "quantity":
        setQuantity((prev) => ({ ...prev, [fieldKey]: value }));
        break;

      case "from_year_to_year":
        setSelectedValue((prev) => ({
          ...prev,
          [fieldKey]: {
            from: value?.from !== undefined ? Number(value.from) : "",
            to: value?.to !== undefined ? Number(value.to) : "",
          },
        }));
        break;

      case "date":
        setStartDate((prev) => ({ ...prev, [fieldKey]: new Date(value) }));
        break;

      case "month_year": {
        const [month, year] = value?.split("/").map(Number) || [];
        setSelectedValueMonth((prev) => ({
          ...prev,
          [fieldKey]: { month: MONTHS[month - 1], year },
        }));
        break;
      }

      case "date_range": {
        const start = parseDateString(value?.start_date);
        const end = parseDateString(value?.end_date);
        if (start) setStartDate((prev) => ({ ...prev, [fieldKey]: start }));
        if (end) setEndDate((prev) => ({ ...prev, [fieldKey]: end }));
        break;
      }

      case "time": {
        const [time, meridiems] = value?.trim()?.split(" ") || [];
        const [hours, minutes] = time?.split(":") || [];
        setTimeData((prev) => ({
          ...prev,
          [fieldKey]: { hours, minutes, meridiems },
        }));
        break;
      }

      default:
        break;
    }
  };

  const applyCopiedValue = ({ name, dataType, value }) => {
    syncPickerState(name, dataType, value);
    handleInputChangeCreate({
      target: {
        name,
        data_type: dataType,
        value: dataType === "quantity" ? Number(value) || 0 : value,
      },
    });
  };

  // Click on a field in the "previous transaction details" panel
  const handlePreviousTransactionClick = (details) => {
    const { name, data_type, value } = details;
    const pasteTargets = PASTE_TARGETS[data_type];

    if (pasteTargets) {
      const copy = {
        ...details,
        value: data_type === "numeric" ? String(value) : value,
      };
      const availableFields =
        txnPayLoad?.selectedCategory?.configuration?.data || [];

      setInitialRefactorAdditionalDetail((prev) => {
        const updated = {};
        Object.entries(prev).forEach(([key, meta]) => {
          updated[key] =
            key === "__labels__"
              ? meta
              : {
                  ...meta,
                  isCopyClick: false,
                  isCopyDirect: false,
                  isCopySource: false,
                  isPaste: {},
                };
        });

        availableFields.forEach((field) => {
          if (pasteTargets.includes(field?.data_type)) {
            updated[field.name] = { ...updated[field.name], isPaste: copy };
          }
        });

        updated[name] = {
          ...updated[name],
          isCopyClick: true,
          isCopyDirect: true,
          isCopySource: true,
        };
        return updated;
      });
      return;
    }

    patchRefactorDetail(name, { isCopyDirect: true });
    applyCopiedValue({ name, dataType: data_type, value });
  };

  useEffect(() => {
    setHaveBankAssociate(bankLookupData?.total_count || 0);
    setHavePANAssociate(panLookupData?.total_count || 0);
  }, [panLookupData, bankLookupData, dispatch]);

  useEffect(() => {
    dispatch(getAllCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!sessionStorage.getItem("internalTxnLabels")) {
      dispatch(
        internalTransactionsViewAll({
          page_no: ONE,
          page_item_count: PaginationConfiguration.paginationSmall,
        }),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    if (!internalCategory) return;
    setTxnPayLoad((prev) => ({
      ...prev,
      selectedCategory: internalCategory,
      mergeCategory: internalCategory?.configuration?.data || null,
    }));
  }, [internalCategory]);

  useEffect(() => {
    setSelectedLabelsCreate(uniq(txnPayLoad?.ownerLabels, txnPayLoad?.labels));
  }, [txnPayLoad?.labels, txnPayLoad?.ownerLabels]);

  // entered values -> mergeCategory (+ drop stale "copied" flags for emptied fields)
  useEffect(() => {
    const fields = txnPayLoad?.selectedCategory?.configuration?.data;
    if (!additionalData || !fields) return;

    fields.forEach(({ name }) => {
      const meta = initialRefactorAdditionalDetail?.[name];
      if (
        isEmptyValue(additionalData[name]) &&
        meta?.isCopyDirect &&
        !meta?.isCopySource
      ) {
        patchRefactorDetail(name, RESET_COPY_STATE);
      }
    });

    setTxnPayLoad((prev) => ({
      ...prev,
      mergeCategory: buildMergeCategory(fields, additionalData),
    }));
  }, [additionalData, txnPayLoad?.selectedCategory]);

  // "copy" buttons (time/date/quantity/toggle/month/year-range/date-range)
  useEffect(() => {
    const { title, dataType, value } = copyData;
    if (value === "" || title === "" || !PICKER_COPY_TYPES.includes(dataType))
      return;

    applyCopiedValue({ name: title, dataType, value });
    setCopyData(EMPTY_COPY_DATA);
  }, [copyData]);

  // pasting a copied value into hyperlink fields
  useEffect(() => {
    Object.entries(initialRefactorAdditionalDetail || {}).forEach(
      ([key, meta]) => {
        if (meta?.isCopyClick && meta?.isPaste?.data_type === "hyperlink") {
          const value = meta.isPaste.value ?? "";
          setHyperLink((prev) => ({ ...prev, [`${key}_hyperlink`]: value }));
          handleInputChangeCreate({
            target: { name: key, value, data_type: "hyperlink" },
          });
          patchRefactorDetail(key, { isCopyClick: false, isPaste: {} });
        }
      },
    );
  }, [initialRefactorAdditionalDetail]);

  // mark fields as "copied directly" while they still equal the old txn
  useEffect(() => {
    if (!additionalData || !initialRefactorAdditionalDetail) return;

    setInitialRefactorAdditionalDetail((prev) => {
      const updated = { ...prev };

      Object.entries(additionalData).forEach(([key, currentValue]) => {
        if (!Object.prototype.hasOwnProperty.call(prev, key)) return;

        const isEqual = areAdditionalValuesEqual(currentValue, prev[key]?.data);
        const isDateRange =
          currentValue &&
          typeof currentValue === "object" &&
          "start_date" in currentValue;

        updated[key] = {
          ...prev[key],
          isCopyDirect: isEqual,
          ...(isEqual && isDateRange ? { isPaste: {} } : {}),
          ...(isEqual && !Array.isArray(currentValue)
            ? { isCopyClick: true }
            : {}),
        };
      });

      return updated;
    });
  }, [additionalData]);

  // un-mark copied labels as soon as they diverge from the old txn
  useEffect(() => {
    if (!labelsCopied) return;

    const previous = [...prevLabels].sort();
    const current = [
      ...(txnPayLoad?.labels || []),
      ...(txnPayLoad?.ownerLabels || []),
    ].sort();

    const stillMatches =
      previous.length === current.length &&
      previous.every((l, i) => l === current[i]);

    if (!stillMatches)
      patchRefactorDetail("__labels__", { isCopyDirect: false });
  }, [txnPayLoad?.labels, txnPayLoad?.ownerLabels]);

  useEffect(() => {
    setRefactorCopyAmount(
      Number(refactorTransactionData?.amount) === Number(txnPayLoad?.amount),
    );
  }, [txnPayLoad.amount]);

  useEffect(() => {
    const prevMethod = refactorTransactionData?.payment_method?.toUpperCase();
    const currentMethod = txnPayLoad?.settleType?.toUpperCase();
    if (!currentMethod) return;
    setRefactorPaymentMethod(currentMethod === prevMethod);
  }, [txnPayLoad?.settleType]);

  // load previous txn when re-creating
  useEffect(() => {
    if (!recreateTxnId) return;

    setRefactor(true);
    dispatch(internalTransactionsDetailsById({ id: recreateTxnId }));
  }, [dispatch, recreateTxnId]);

  useEffect(() => {
    if (internalTransactionsDetailsLoading || !refactor) return;

    const transaction = internalTransactionsDetailsData?.transaction?.[0];

    // Only accept the transaction we asked for, never stale store data.
    if (!transaction || transaction.txn_id !== recreateTxnId) return;

    setInitialRefactorAdditionalDetail(
      buildInitialAdditionalDetails(transaction?.additional_data?.data),
    );
    if (transaction?.confidential_transaction === "Y") setIsConfidential(true);

    setRefactorTransactionData(transaction);
    const method = transaction?.payment_method?.toUpperCase();
    setIsNeft(method === "NEFT");
    setIsRtgs(method === "RTGS");
    setRefactorPaymentMethod(method === "NEFT" || method === "RTGS");
  }, [
    internalTransactionsDetailsData,
    internalTransactionsDetailsLoading,
    refactor,
    recreateTxnId,
  ]);

  useEffect(() => {
    if (internalTransactionsDetailsLoading || !refactor) return;
    setRefactorAccountDetail(
      txnPayLoad?.ownerId === refactorTransactionData?.owner_account_id,
    );
  }, [
    txnPayLoad?.ownerId,
    refactorTransactionData?.owner_account_id,
    internalTransactionsDetailsLoading,
    refactor,
  ]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 800);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // enable / disable "Next"
  useEffect(() => {
    const {
      beneAccountId,
      ownerId,
      amount,
      settleType,
      selectedCategory,
      mergeCategory,
    } = txnPayLoad;

    const mandatoryFields = (
      selectedCategory?.configuration?.data || []
    ).filter((f) => f.mandatory === "Y");
    const filledMandatory = (mergeCategory || []).filter(
      (f) => f.mandatory === "Y" && isMandatoryFieldFilled(f),
    ).length;
    const hyperlinksValid = (mergeCategory || [])
      .filter((f) => f.data_type === "hyperlink" && f.value !== "")
      .every((f) => isValidHttpUrl(f.value));

    const isOverRtgsLimit = amount > RTGS_MIN_AMOUNT;
    const isPaymentMethodInvalid =
      (settleType === "rtgs" && !isOverRtgsLimit) ||
      ((settleType === "neft" || settleType === "imps") && isOverRtgsLimit);
    const isRtgsInvalid = isRtgs && amount < RTGS_MIN_AMOUNT;

    const stepValidity = [
      Boolean(beneAccountId && ownerId),
      !mandatoryFields.length ||
        (filledMandatory === mandatoryFields.length && hyperlinksValid),
      !!amount && !isRtgsInvalid && !isPaymentMethodInvalid,
      !!settleType,
    ];

    setNextButtonEnabled(stepValidity[activeStep] ?? true);
  }, [activeStep, txnPayLoad, isRtgs, hyperLink, additionalData]);

  useEffect(() => {
    if (autoFill) {
      setIsHoverEnabled(false);
      return;
    }
    const timer = setTimeout(
      () => setIsHoverEnabled(true),
      IntervalConfiguration.intervalSmall,
    );
    return () => clearTimeout(timer);
  }, [autoFill]);

  useEffect(() => {
    const updateHeight = () =>
      setStepperHeight(
        stepperHeightRef?.current?.firstElementChild?.offsetHeight,
      );
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Balance refresh (request, toasts, payload update) is handled inside
  // CommonDestinationAccount. We only clear the stale refresh result on unmount.
  useEffect(() => {
    return () => {
      dispatch(resetOwnerBalance());
    };
  }, []);

  /* ------------------------------ stepper data ---------------------------- */
  const reminderText = reminder_remark
    ? `Reminder remark: ${reminder_remark || "-"}`
    : "";

  const dataArray = steps.map((stepLabel, index) => {
    const isLastStep = index === steps.length - 1;
    const isTxnLoading = isLastStep && isSubmitting;

    return {
      ...COMMON_STEPPER_CONFIG,
      previousText: index === ZERO ? "Cancel" : "Previous",
      previousIsDisabled: isTxnLoading,
      nextText: isLastStep ? (similarTransaction ? "Check" : "Create") : "Next",
      nextLoaderNeeded: isLastStep,
      nextIsLoading: isTxnLoading,
      nextIsEnabled: isLastStep ? !isSubmitting : nextButtonEnabled,
      nextEnableBgColor: isLastStep
        ? similarTransaction
          ? "var(--emphasis)"
          : "var(--positive-act)"
        : "var(--text-primary)",
      nextDisableBgColor: isLastStep
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextEnableTextColor: isLastStep
        ? "var(--text-primary)"
        : "var(--primary-bg)",
      nextDisableTextColor: "var(--text-primary)",
      stepperText: stepLabel,
      extraInfoSubText: reminderText,
      extraInfoShow: reminder_remark,
    };
  });

  /* ------------------------------- renderers ------------------------------ */
  const renderCreateInternalTxn = () => {
    switch (activeStep) {
      case 0:
        return (
          <InternalTransactionStepAccount
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            openOwner={openOwner}
            setOpenOwner={setOpenOwner}
            isConfidential={isConfidential}
            setIsConfidential={setIsConfidential}
            shouldShowAssociatedAccounts={shouldShowAssociatedAccounts}
            handleOwnerSelection={handleOwnerSelection}
            handleOpenInternalAccountModal={handleOpenInternalAccountModal}
            handleOpenAssociatedAccounts={handleOpenAssociatedAccounts}
            associatedAccountsText={associatedAccountsText}
          />
        );
      case 1:
        return (
          <InternalTransactionStepAdditionalDetails
            showLabelModal={showLabelModal}
            setShowLabelModal={setShowLabelModal}
            selectedLabelsCreate={selectedLabelsCreate}
            txnPayLoad={txnPayLoad}
            additionalData={additionalData}
            setAdditionalData={setAdditionalData}
            quantity={quantity}
            setQuantity={setQuantity}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            selectedValueMonth={selectedValueMonth}
            setSelectedValueMonth={setSelectedValueMonth}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            hyperLink={hyperLink}
            setHyperLink={setHyperLink}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            previewImages={previewImages}
            setPreviewImages={setPreviewImages}
            timeData={timeData}
            setTimeData={setTimeData}
            initialRefactorAdditionalDetail={initialRefactorAdditionalDetail}
            setInitialRefactorAdditionalDetail={
              setInitialRefactorAdditionalDetail
            }
            reminder_remark={reminder_remark}
            flow={flow}
            refactor={refactor}
          />
        );
      case 2:
        return (
          <InternalTransactionStepAmount
            selectedRadioValue={selectedRadioValue}
            txnPayLoad={txnPayLoad}
            isRtgs={isRtgs}
            amountInWords={amountInWords}
            reminder_remark={reminder_remark}
            flow={flow}
            copyData={copyData}
            setCopyData={setCopyData}
            COPY_KEYS={COPY_KEYS}
            slideAnimation={slideAnimation}
            transactionLoading={transactionLoading}
            handleNeftClick={handleNeftClick}
            handleRtgsClick={handleRtgsClick}
            handleAmountChange={handleAmountChange}
            handleDivBtn={handleDivBtn}
          />
        );
      case 3:
        return (
          <InternalTransactionStepConfirm
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            isConfidential={isConfidential}
            previewImages={previewImages}
            uploadedFiles={uploadedFiles}
            allLabels={allLabels}
          />
        );
      default:
        return null;
    }
  };

  const renderRecreateInternalTxn = () => {
    switch (activeStep) {
      case ZERO:
        return (
          <InternalRecreateTxnStepSourceDestination
            config={config}
            internalTransactionsDetailsData={internalTransactionsDetailsData}
            refactorTransactionData={refactorTransactionData}
            refactorAccountDetail={refactorAccountDetail}
            refactorDestinationAccountDetail={refactorDestinationAccountDetail}
            txnPayLoad={txnPayLoad}
            isConfidential={isConfidential}
            setIsConfidential={setIsConfidential}
            setAutoFill={setAutoFill}
            handleDestinationAccountSelection={
              handleDestinationAccountSelection
            }
            handleSourceAccountSelection={handleSourceAccountSelection}
          />
        );
      case ONE:
        return (
          <InternalRecreateTxnStepAdditionalDetails
            refactorTransactionData={refactorTransactionData}
            txnPayLoad={txnPayLoad}
            initialRefactorAdditionalDetail={initialRefactorAdditionalDetail}
            setInitialRefactorAdditionalDetail={
              setInitialRefactorAdditionalDetail
            }
            setAutoFill={setAutoFill}
            onCopyLabels={handleCopyLabels}
            displayLabels={displayLabels}
            prevLabels={prevLabels}
            labelsCopied={labelsCopied}
            handlePreviousTransactionClick={handlePreviousTransactionClick}
          />
        );
      case TWO:
        return (
          <InternalRecreateTxnStepAmountPaymentMethod
            refactorTransactionData={refactorTransactionData}
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            refactorCopyAmount={refactorCopyAmount}
            setRefactorCopyAmount={setRefactorCopyAmount}
            COPY_KEYS={COPY_KEYS}
            refactorPaymentMethod={refactorPaymentMethod}
            setRefactorPaymentMethod={setRefactorPaymentMethod}
            setIsNeft={setIsNeft}
            setIsRtgs={setIsRtgs}
            setAutoFill={setAutoFill}
            setCopyData={setCopyData}
          />
        );
      default:
        return null;
    }
  };

  const topNavigation = (
    <TopNavComponent
      topNavHeader="Internal transaction"
      topNavSubText={steps[activeStep] || ""}
      isFormattingNotRequired
    />
  );

  const showRecreateOnTop = refactor && [ZERO, ONE, TWO].includes(activeStep);

  return (
    <>
      {isInternalAccountPopupOpen && (
        <InternalAccountViewAllPopup
          typepop="popup"
          flow="internalTxn"
          onClosepop={closeInternalAccountPopup}
          handleButtonClick={handleInternalAccountSelection}
          txnPayLoad={txnPayLoad}
          setTxnPayLoad={setTxnPayLoad}
          beneAccountId={
            isAssociatedMode ? undefined : txnPayLoad?.beneAccountId
          }
          setIsNeft={setIsNeft}
          setIsImps={{}}
          setIsRtgs={setIsRtgs}
          activeType={activeType}
          setActiveType={setActiveType}
          remPan={isAssociatedMode && activeType !== "pan" ? "" : removePan}
          remAccount={
            isAssociatedMode && activeType !== "bank" ? "" : removeAccount
          }
          remIFSC={isAssociatedMode && activeType !== "bank" ? "" : removeIfsc}
          setRemPan={setRemovePan}
          setRemAccount={setRemoveAccount}
          setRemIFSC={setRemoveIfsc}
          {...(isAssociatedMode && {
            ispanbankRequired: true,
            typePanBank: "BeneAccPanBankSwitch",
            associatepanNumber: txnPayLoad?.pan,
            associateaccountNumber: txnPayLoad?.bankAccountNo,
            associateIFSC: txnPayLoad?.bankAccountIFSC,
          })}
        />
      )}

      {showLabelModal && (
        <div className="fixed left-left-base w-width-full top-top-base h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity flex items-center justify-center z-max-zindex">
          <CommonAssignLabelPopup
            headerTitle="Assign/unassign labels:"
            headersubtext="Labels will be used to generate statistics for the associated transactions."
            closehandleFunction={handleClose}
            closeisDisabled={false}
            labels={Array.isArray(labels) ? labels : []}
            setLabels={setLabels}
            selectedLabelsCreate={selectedLabelsCreate}
            setSelectedLabelsCreate={setSelectedLabelsCreate}
            isLabelPopupOpen={showLabelModal}
            loading={labelPopupLoading}
            setLoading={setLabelPopupLoading}
            type="create"
          />
        </div>
      )}

      {showSimilarModal && (
        <InternalTransactionViewAllPopup
          isOpen={showSimilarModal}
          onClose={() => setShowSimilarModal(false)}
          showBackdrop
          title="Similar transactions found"
          headerIcon={ICONS.ALERT}
          subtitle="Transactions with similar details have already been created. Proceeding will result in a duplicate transaction."
          showFooter
          footerTitle="Proceed with creating this transaction?"
          footerButtonText="Proceed with caution"
          onFooterButtonClick={() => {
            setShowSimilarModal(false);
            handleCreateTransaction();
          }}
          contentProps={{
            txnHistorytype: "internalCreateTxn",
            duplicateFilters: buildDuplicateCheckPayload(),
            internal_account_bank_acc_no: txnPayLoad?.bankAccountNo,
            bank_acc_no: txnPayLoad?.ownerBankAccountNo,
            txnPayLoad,
            onClose: () => setShowSimilarModal(false),
          }}
        />
      )}

      {transactionLoading && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-light-opacity">
          <div>
            <Loader type="bulk_upload" />
            <p className="font-semibold text-font-lg text-text-primary mt-margin-sm">
              Validating the data
            </p>
          </div>
        </div>
      )}
      {showBankingHoursPopup && (
        <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop bg-opacity-dark-opacity justify-center z-z-index-modal">
          <SmallPopupWithBulletPoints
            headerTitle="Why transaction cannot be processed?"
            headerFontWeight="semibold"
            headerFontColor="negative-act"
            textSize="font-md"
            bulletPoints={[
              `Any transaction created or approved after banking hours i.e. between ${getOutsideBankingHoursLabel()} will be processed on the next bank working day.`,
              "Payments are time-critical (e.g., salary, vendor payment, or bill payment), consider the possible processing delay before proceeding.",
            ]}
            closeIconImage={ICONS?.CLOSE}
            closeModal={() => setShowBankingHoursPopup(false)}
          />
        </div>
      )}
      {showConfirmLeavePopup && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle={globalText?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-lg"
            subtext={globalText?.EXIT_CURRENT_FLOW_NOTE}
            btnOneText={globalText?.STAY}
            btnOneHandleFunction={() => setShowConfirmLeavePopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText={globalText?.LEAVE}
            btnTwoHandleFunction={() => {
              setShowConfirmLeavePopup(false);
              leavePage();
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
          />
        </div>
      )}
      {refactor && autoFill && (
        <div
          className={`absolute top-top-base left-left-base w-full h-screen justify-center items-center z-z-index-fixed-ui ${isMobile ? "flex" : "hidden"}`}
        >
          <div className="flex flex-col h-full justify-center w-full">
            <div className="flex px-padding-sm justify-center w-full">
              <div className="w-max-content-width flex justify-center items-center relative">
                {renderRecreateInternalTxn()}
              </div>
            </div>
          </div>
        </div>
      )}
      {reminder_remark && showReminderRemark && (
        <div className="w-full-width h-full-height Qsmd:hidden overflow-x-hidden">
          <div className="fixed top-top-base left-left-base mt-margin-base w-width-full rounded-bl-border-radius-md rounded-br-border-radius-md z-z-index-header bg-primary-nav shadow-box-shadow-bottom lg:hidden block">
            {topNavigation}
          </div>

          <div className="flex flex-col p-padding-md gap-y-gap-sm mt-margin-xxl">
            <p className="text-font-xs text-text-secondary font-sans">
              Reminder remark
            </p>
            <p className="text-font-sm text-text-primary font-sans font-normal pb-padding-xl break-words">
              {reminder_remark}
            </p>
          </div>
          <div
            className="w-width-full h-auto cursor-pointer bottom-bottom-base fixed py-padding-sm flex justify-center items-center bg-text-primary"
            onClick={() => setShowReminderRemark(false)}
          >
            <p className="text-font-md text-primary-nav font-sans font-semibold">
              Back
            </p>
          </div>
        </div>
      )}
      <div
        className={`${showReminderRemark ? "hidden Qsmd:flex" : "flex"} w-width-full justify-start items-center flex-col bg-primary-bg overflow-hidden h-screen flex`}
      >
        <div className="w-full Qsmd:block hidden lg:bg-transparent bg-primary-nav">
          <div className="h-navbar-breadcrumb-height my-margin-sm Qsmd:my-margin-base"></div>
        </div>

        <div
          style={isWindow && isReminderFlow ? { minHeight: stepperHeight } : {}}
          className={`flex justify-center Qsmd:items-end w-screen Qsmd:relative fixed bottom-bottom-base left-left-base z-z-index-loader ${
            reminder_remark !== null && isReminderFlow
              ? " min-h-stepper-moble-height"
              : refactor
                ? "Qsmd:min-h-[calc(var(--stepper-height)+var(--icon-sm))] min-h-stepper-moble-height Qsmd:bg-transparent"
                : "min-h-[var(--stepper-moble-height)]"
          }`}
        >
          <div
            ref={stepperHeightRef}
            className="flex flex-col items-center justify-center"
          >
            <PrimaryStepper
              activeStep={activeStep}
              handleForwardAction={handleNext}
              handleBackwardAction={handleBack}
              dataArray={dataArray}
            />
          </div>
        </div>

        <div
          className={`Qsmd:flex w-full justify-center items-center rounded-b-border-radius-md lg:max-w-tab-width-normal mx-auto relative group Qsmd:top-[calc(var(--negative-global-var)*var(--padding-md))] ${refactor ? "Qsmd:mb-[calc(var(--negative-global-var)*var(--margin-md))]" : ""} ${!reminder_remark ? "bg-primary-card" : ""} ${showRecreateOnTop || reminder_remark ? "Qmd:flex" : "hidden"} ${activeStep === THREE && "!hidden"}`}
          style={{ transition: "height var(--animation-fast) ease" }}
        >
          {refactor && (
            <div className="flex flex-col w-full">
              <div
                className={`Qsmd:block hidden relative w-width-full py-padding-sm bg-primary-card max-w-tab-width-normal z-z-index-base ${isHoverEnabled ? "group-hover:bg-primary-nav-act" : ""}`}
              ></div>
              <div className="flex flex-col h-full justify-center w-full">
                <div
                  className={`flex justify-start w-full ${activeStep === ONE ? "pl-padding-md" : "px-padding-md"}`}
                >
                  <div className="overflow-hidden w-max-content-width flex justify-start relative">
                    <div
                      className={`autofill-wrapper w-width-full !hidden Qsmd:!flex ${
                        !hasAutoFillInteracted.current
                          ? ""
                          : autoFill
                            ? "open-wrap"
                            : "close-wrap"
                      }`}
                    >
                      {renderRecreateInternalTxn()}
                    </div>
                  </div>
                </div>
                {autoFill && (
                  <hr
                    style={{
                      width: "var(--slider-items-seperator-line-width)",
                      maxWidth: "var(--max-content-width)",
                      alignSelf: "center",
                      border: "none",
                      borderTop: "var(--border) solid var(--primary-bg)",
                    }}
                  />
                )}
                {internalTransactionsDetailsLoading ? (
                  <div className="w-width-full flex justify-center relative global-hover-white z-z-index-base py-padding-sm rounded-b-border-radius-md">
                    <Loader
                      height="var(--loader-sm)"
                      width="var(--loader-sm)"
                    />
                  </div>
                ) : (
                  <div
                    className="flex items-center h-full justify-center gap-gap-sm py-padding-sm global-hover-white z-z-index-base relative rounded-b-border-radius-md cursor-pointer"
                    style={{ display: isMobile ? "none" : "flex" }}
                    onClick={toggleAutoFill}
                  >
                    <p className="text-text-primary text-sm font-normal">
                      Copy or autofill from previous transactions details
                    </p>
                    <SvgComponent
                      url={
                        autoFill
                          ? ICONS?.ARROW_UP_WHITE
                          : ICONS?.ARROW_DOWN_WHITE
                      }
                      color="var(--text-primary)"
                      height="var(--icon-sm)"
                      width="var(--icon-sm)"
                      alt="Arrow"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="fixed top-top-base left-left-base mt-margin-base w-width-full rounded-bl-border-radius-md rounded-br-border-radius-md z-z-index-header bg-primary-nav shadow-box-shadow-bottom lg:hidden block">
          {topNavigation}
        </div>
        <div className="h-loader-lg Qsmd:hidden block flex-shrink-0"></div>
        <div
          ref={containerRef}
          className="w-width-full h-auto overflow-y-auto overflow-x-hidden flex-1"
        >
          <div
            className={`${
              reminder_remark
                ? isMobile
                  ? "pt-padding-base"
                  : ""
                : !refactor
                  ? "max-w-screen-lg mx-auto h-full flex flex-col items-center relative w-width-full"
                  : activeStep === ONE
                    ? "Qmd:mb-auto"
                    : activeStep === TWO || activeStep === THREE
                      ? ""
                      : "Qmd:mb-auto Qsmd:pb-padding-md"
            }`}
          >
            {shouldShowBankingHoursAlert && (
              <div className="max-w-tab-width-normal Qlg:mx-auto pt-padding-md Qlg:px-padding-base px-padding-md w-width-full">
                <CommonAlertCard
                  title="Functioning outside banking hours"
                  texts="Transactions will be processed automatically during the next bank working day. Learn more"
                  highlightedText={["Learn more"]}
                  onHighlightedTextClick={(event, index) => {
                    if (index === 0) setShowBankingHoursPopup(true);
                  }}
                />
              </div>
            )}
            <div
              className={`flex Qsmd:flex-row flex-col w-width-full max-w-tab-width-normal mx-auto ${
                (activeStep === ZERO && direction === "next") || removeClassName
                  ? ""
                  : animationClassS
              }`}
            >
              {renderCreateInternalTxn()}
            </div>
            <div
              className={`w-full bg-primary-nav-act global-hover-white fixed bottom-bottom-base cursor-pointer z-z-index-header ${!autoFill && refactor && activeStep !== THREE ? "Qsmd:hidden block" : "hidden"}`}
            >
              <div
                className="w-full left-left-base"
                onClick={toggleAutoFill}
              >
                <div className="flex justify-between p-padding-md">
                  <p className="font-semibold text-text-primary text-font-xs">
                    Copy or autofill from previous transactions details
                  </p>
                  <SvgComponent
                    url={ICONS?.ARROW_RIGHT_WHITE}
                    color="var(--text-primary)"
                    height="var(--icon-sm)"
                    width="var(--icon-sm)"
                    alt="Arrow"
                  />
                </div>
                <div className="h-stepper-moble-height Qsmd:h-mobile-create-header w-full Qsmd:hidden block"></div>
              </div>
            </div>
          </div>
        </div>
        {reminder_remark && isMobile && (
          <div className="fixed w-full bottom-bottom-base z-z-index-toast flex-flex-col pointer-events-none bg-primary-nav-act global-hover-white">
            <div className="w-full-width pointer-events-auto cursor-pointer">
              <div
                className="flex flex-row justify-between w-width-full p-padding-md top-top_xxs left-left-base"
                onClick={() => setShowReminderRemark(true)}
              >
                <p className="text-font-xs text-text-primary font-sans">
                  Reminder remark
                </p>
                <SvgComponent
                  url={ICONS?.ARROW_RIGHT_WHITE}
                  color="var(--text-primary)"
                  height="var(--icon-sm)"
                  width="var(--icon-sm)"
                  alt="Arrow"
                />
              </div>
            </div>
            <div className="h-stepper-moble-height w-full Qsmd:hidden block"></div>
          </div>
        )}
        {(reminder_remark || refactor) &&
          !(refactor && activeStep === THREE) && (
            <div className="h-mobile-create-header Qsmd:hidden block"></div>
          )}
        <div className="h-stepper-moble-height Qsmd:hidden block"></div>
      </div>
    </>
  );
};

export default InternalTransactionCreate;
